import { useState } from 'preact/hooks';
import { acceptBrowserConsent } from '../api/settings';
import { getBrand } from '../brands';

interface Props {
  /** Version string returned by the server with BROWSER_CONSENT_REQUIRED (audit trail only) */
  version: string;
  /** Called after the user accepts and the server records consent */
  onAccepted: () => void;
  /** Called when the user declines or dismisses the modal */
  onDeclined: () => void;
}

/**
 * Phase 2 of plans/browser-mcp-oauth-custody-fix.md — shown when the backend
 * detects browser intent but the user hasn't accepted the current consent copy.
 *
 * Triggered by the BROWSER_CONSENT_REQUIRED WebSocket event. After acceptance,
 * the user can re-issue the prompt and the gateway will let it through.
 */
export function BrowserConsentModal({ version, onAccepted, onDeclined }: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAccept() {
    setSaving(true);
    setError(null);
    try {
      await acceptBrowserConsent();
      onAccepted();
    } catch (err) {
      setError('Failed to save your consent. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: '16px',
    }}>
      <div style={{
        background: 'var(--bg-card, #1e1e2e)',
        border: '1px solid var(--border, #313244)',
        borderRadius: '12px',
        maxWidth: '480px',
        width: '100%',
        padding: '28px 24px',
        color: 'var(--fg, #cdd6f4)',
        maxHeight: '90svh',
        overflowY: 'auto',
      }}>
        <h2 style={{ margin: '0 0 12px', fontSize: '1.2rem', fontWeight: 700 }}>
          You're about to give the agent a browser.
        </h2>

        <p style={{ margin: '0 0 16px', fontSize: '0.9rem', lineHeight: 1.5 }}>
          Anything the agent does in this browser — including sites you sign into —
          runs on {getBrand().name}'s servers. Your browser profile persists across sessions
          (so you stay logged in) and is visible to no one but you, but:
        </p>

        <ul style={{ margin: '0 0 16px 0', padding: '0 0 0 20px', fontSize: '0.875rem', lineHeight: 1.55, color: 'var(--fg-muted, #a6adc8)' }}>
          <li>Sign in only to accounts you're comfortable being stored in this sandbox.</li>
          <li>Prefer fine-grained tokens over full OAuth when possible.</li>
          <li>You can wipe all browser state anytime from Settings → Browser.</li>
        </ul>

        <p style={{ margin: '0 0 20px', fontSize: '0.85rem', color: 'var(--fg-muted, #a6adc8)' }}>
          By clicking Accept, you consent to the browser processing described in our{' '}
          <a href="/legal/privacy.html" target="_blank" rel="noopener noreferrer"
             style={{ color: 'var(--accent, #89b4fa)', textDecoration: 'underline' }}>
            Privacy Policy
          </a>
          .
        </p>

        {error && (
          <div style={{ color: 'var(--red, #f38ba8)', fontSize: '0.85rem', marginBottom: '12px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
          <button
            onClick={onDeclined}
            disabled={saving}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid var(--border, #313244)',
              background: 'transparent',
              color: 'var(--fg, #cdd6f4)',
              fontWeight: 600,
              fontSize: '0.9rem',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            disabled={saving}
            style={{
              flex: 1.4,
              padding: '12px',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--accent, #89b4fa)',
              color: '#1e1e2e',
              fontWeight: 700,
              fontSize: '0.9rem',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Accept — turn on browser'}
          </button>
        </div>

        <div style={{ marginTop: '12px', fontSize: '0.7rem', color: 'var(--fg-muted, #a6adc8)', textAlign: 'center' }}>
          Consent version: {version}
        </div>
      </div>
    </div>
  );
}
